import { isVehicleSize, VEHICLE_SIZES, type VehicleSize } from '@mustauto/types/esthetic-packages';
import {
  hasStockPrecision,
  isValidStockQuantity,
  parseStockQuantityText,
  roundStockQuantity,
  STOCK_QUANTITY_MAX,
} from './stock-quantity';

/**
 * Insumos do pacote de estética (F2.6) — regras puras, as mesmas na API, no
 * painel e no app.
 *
 * A RECEITA mora no pacote: "a vitrificação gasta 30 ml de vitrificador e
 * 50 ml de shampoo". A OS guarda só o AJUSTE do detailer ("hoje foram 40 ml"),
 * e o que baixa do estoque na conclusão é a receita com o ajuste por cima.
 *
 * Ajuste por cima, e não cópia da receita na abertura, por dois motivos:
 *  - a OS nasce offline no app, que pode ter a lista de pacotes em cache de
 *    antes da receita existir — a cópia sairia vazia e nada baixaria;
 *  - pacote acrescentado à OS depois do ajuste traz os insumos dele: o ajuste
 *    só fala dos itens que o detailer mexeu.
 *
 * Insumo não é cobrança: já está no preço do pacote. Não vira item da OS.
 */

export interface PackageConsumable {
  inventoryItemId: string;
  /** Na unidade do item de estoque (ml, g, L…), até 3 casas. */
  quantity: number;
  /**
   * Só na RECEITA: quanto gasta em cada porte, quando difere da base — um SUV
   * gasta mais vitrificador que um hatch. Porte ausente = `quantity`; zero =
   * aquele porte não usa. Mesmo desenho do preço por porte do pacote.
   */
  sizeQuantities?: Partial<Record<VehicleSize, number>>;
}

/** A quantidade da receita para o porte da OS. Sem porte, ou porte sem valor próprio: a base. */
export function consumableQuantityForSize(c: PackageConsumable, size: string | null | undefined): number {
  if (size && isVehicleSize(size)) {
    const q = c.sizeQuantities?.[size];
    if (typeof q === 'number') return q;
  }
  return c.quantity;
}

function isStockAmount(n: number): boolean {
  return Number.isFinite(n) && n >= 0 && n <= STOCK_QUANTITY_MAX && hasStockPrecision(n);
}

function readSizeQuantities(raw: unknown): Partial<Record<VehicleSize, number>> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Partial<Record<VehicleSize, number>> = {};
  for (const size of VEHICLE_SIZES) {
    const v = (raw as Record<string, unknown>)[size];
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    if (isStockAmount(n)) out[size] = roundStockQuantity(n);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** O insumo como a OS o mostra: o que a receita diz e o que vai baixar. */
export interface ResolvedConsumable {
  inventoryItemId: string;
  /** Soma das receitas dos pacotes da OS. */
  recipeQuantity: number;
  /** O que baixa na conclusão: o ajuste, ou a receita se não houve ajuste. */
  quantity: number;
  /** O detailer mudou este item. */
  adjusted: boolean;
}

/** Teto de itens por receita e por ajuste. */
export const PACKAGE_CONSUMABLES_MAX = 30;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Lê a lista do banco ou do aparelho sem confiar nela: id de item válido,
 * quantidade finita, não negativa e com até 3 casas (arredondada). Item
 * repetido: vale o último. Lixo vira `[]`.
 *
 * `allowZero`: no AJUSTE, zero é "não usei" e precisa ficar; na RECEITA, zero
 * não diz nada e sai.
 */
export function readConsumables(raw: unknown, { allowZero = false }: { allowZero?: boolean } = {}): PackageConsumable[] {
  if (!Array.isArray(raw)) return [];
  const byId = new Map<string, { quantity: number; sizeQuantities?: Partial<Record<VehicleSize, number>> }>();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { inventoryItemId, quantity, sizeQuantities } = entry as Record<string, unknown>;
    if (typeof inventoryItemId !== 'string' || !UUID.test(inventoryItemId)) continue;
    const n = typeof quantity === 'number' ? quantity : typeof quantity === 'string' ? Number(quantity) : NaN;
    if (!isStockAmount(n)) continue;
    if (n === 0 && !allowZero) continue;
    // Teto ANTES de inserir: item novo além do limite fica de fora (repetido
    // de um que já está, não conta — só troca o valor).
    if (!byId.has(inventoryItemId) && byId.size >= PACKAGE_CONSUMABLES_MAX) continue;
    byId.delete(inventoryItemId);
    byId.set(inventoryItemId, { quantity: roundStockQuantity(n), sizeQuantities: readSizeQuantities(sizeQuantities) });
  }
  return Array.from(byId, ([inventoryItemId, { quantity, sizeQuantities: sizes }]) => ({
    inventoryItemId,
    quantity,
    ...(sizes ? { sizeQuantities: sizes } : {}),
  }));
}

/** Teto de repetições de um mesmo pacote na OS (quantidade da linha). */
const PACKAGE_REPEAT_MAX = 10;

/**
 * Os pacotes da OS: os itens de mão de obra que nasceram de um pacote, um id
 * POR APLICAÇÃO — a linha com quantidade 2 (ou duas linhas do mesmo pacote)
 * gasta a receita duas vezes.
 */
export function packageIdsFromItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  const ids: string[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const { packageId, quantity } = item as Record<string, unknown>;
    if (typeof packageId !== 'string' || !packageId) continue;
    const q = typeof quantity === 'number' && Number.isFinite(quantity) ? Math.round(quantity) : 1;
    const times = Math.min(Math.max(q, 1), PACKAGE_REPEAT_MAX);
    for (let i = 0; i < times; i++) ids.push(packageId);
  }
  return ids;
}

/**
 * O ajuste como texto JSON — a coluna crua do WatermelonDB e o campo do sync.
 * `undefined` = ausente ou ilegível (não mexer); `null` = sem ajuste (a
 * receita). Nunca lança: JSON quebrado não derruba o push da fila offline.
 */
export function parseConsumablesJson(raw: string | null | undefined): PackageConsumable[] | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null || raw.trim() === '') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null) return null;
    return Array.isArray(parsed) ? readConsumables(parsed, { allowZero: true }) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * O ajuste a gravar a partir do que a tela mostra: SÓ o que difere da
 * receita. Salvar a lista inteira fixaria o que o detailer nem tocou — o
 * shampoo que ficou em 50 continuaria 50 depois que outro pacote com shampoo
 * entrasse na OS, e a diferença nunca baixaria. Nada diferente = `null`
 * (volta à receita).
 */
export function adjustmentFromEdits(
  rows: readonly { inventoryItemId: string; recipeQuantity: number }[],
  quantities: ReadonlyMap<string, number>,
): PackageConsumable[] | null {
  const out: PackageConsumable[] = [];
  for (const r of rows) {
    const q = quantities.get(r.inventoryItemId);
    if (q === undefined || q === r.recipeQuantity) continue;
    out.push({ inventoryItemId: r.inventoryItemId, quantity: roundStockQuantity(q) });
  }
  return out.length > 0 ? out : null;
}

/**
 * A receita da OS: a soma das receitas dos pacotes, na quantidade do PORTE
 * com que a OS foi aberta. Dois pacotes que usam o mesmo shampoo somam — é o
 * que sai do frasco. `packageIds` com repetição (o mesmo pacote duas vezes na
 * OS) conta duas vezes. Item cujo porte é zero não entra.
 */
export function recipeForPackages(
  packageIds: readonly string[],
  recipes: ReadonlyMap<string, readonly PackageConsumable[]>,
  size?: string | null,
): PackageConsumable[] {
  const total = new Map<string, number>();
  for (const id of packageIds) {
    for (const c of recipes.get(id) ?? []) {
      const q = consumableQuantityForSize(c, size);
      if (q <= 0) continue;
      total.set(c.inventoryItemId, roundStockQuantity((total.get(c.inventoryItemId) ?? 0) + q));
    }
  }
  return Array.from(total, ([inventoryItemId, quantity]) => ({ inventoryItemId, quantity }));
}

/**
 * Receita + ajuste = o que a OS vai baixar. Sem ajuste (`null`), a receita
 * como está. Com ajuste, o item ajustado vale o ajuste (zero = não usou) e o
 * item da receita que o ajuste não cita continua pela receita.
 *
 * O ajuste só fala de item DA RECEITA: o que ele cita fora dela é ignorado.
 * Tirar o pacote da OS depois de ajustar tira os insumos dele junto — senão o
 * vitrificador ajustado de um pacote que saiu continuaria baixando.
 */
export function resolveConsumables(
  recipe: readonly PackageConsumable[],
  adjustment: readonly PackageConsumable[] | null | undefined,
): ResolvedConsumable[] {
  const adjusted = new Map((adjustment ?? []).map((c) => [c.inventoryItemId, c.quantity]));
  const out: ResolvedConsumable[] = recipe.map((c) => {
    const override = adjusted.get(c.inventoryItemId);
    return {
      inventoryItemId: c.inventoryItemId,
      recipeQuantity: c.quantity,
      quantity: override ?? c.quantity,
      adjusted: override !== undefined && override !== c.quantity,
    };
  });
  return out;
}

/** O que efetivamente sai do estoque: só quantidade positiva. */
export function consumablesToDeduct(resolved: readonly ResolvedConsumable[]): PackageConsumable[] {
  return resolved
    .filter((c) => c.quantity > 0)
    .map((c) => ({ inventoryItemId: c.inventoryItemId, quantity: c.quantity }));
}

/**
 * Uma linha da receita no editor de pacote — painel e app do gestor. As
 * quantidades são texto até salvar ("0,5", vazio).
 */
export interface ConsumableDraft {
  inventoryItemId: string;
  quantity: string;
  /** Quantidade por porte, como texto; vazio = a base. */
  sizes?: Partial<Record<VehicleSize, string>>;
  /**
   * Os campos por porte estão abertos. Fechados, NÃO valem: "mesma para todo
   * porte" é isso — o que ficou digitado nos campos escondidos não vai junto.
   */
  showSizes?: boolean;
}

/** Quantidade por porte válida: zero ("esse porte não usa") ou positiva com até 3 casas. */
function isSizeAmount(q: number): boolean {
  return q === 0 || isValidStockQuantity(q);
}

/** O texto de cada porte com valor, só com os campos por porte abertos. */
function draftSizeTexts(d: ConsumableDraft): [VehicleSize, string][] {
  if (!d.showSizes) return [];
  return VEHICLE_SIZES.flatMap((size) => {
    const text = d.sizes?.[size]?.trim();
    return text ? [[size, text] as [VehicleSize, string]] : [];
  });
}

/** O campo do porte tem texto que não é quantidade. */
export function isInvalidSizeText(text: string | undefined): boolean {
  return !!text && text.trim() !== '' && !isSizeAmount(parseStockQuantityText(text));
}

/** A receita pronta para a API — só as linhas completas e válidas. */
export function consumablesFromDrafts(drafts: readonly ConsumableDraft[]): PackageConsumable[] {
  return drafts
    .filter((d) => d.inventoryItemId && isValidStockQuantity(parseStockQuantityText(d.quantity)))
    .map((d) => {
      const sizeQuantities: Partial<Record<VehicleSize, number>> = {};
      for (const [size, text] of draftSizeTexts(d)) {
        const q = parseStockQuantityText(text);
        if (isSizeAmount(q)) sizeQuantities[size] = q;
      }
      return {
        inventoryItemId: d.inventoryItemId,
        quantity: parseStockQuantityText(d.quantity),
        ...(Object.keys(sizeQuantities).length > 0 ? { sizeQuantities } : {}),
      };
    });
}

/** Alguma linha começada e inválida? O formulário não salva pela metade. */
export function hasInvalidConsumableDraft(drafts: readonly ConsumableDraft[]): boolean {
  return drafts.some((d) => {
    if (!d.inventoryItemId && !d.quantity.trim()) return false;
    return (
      !d.inventoryItemId ||
      !isValidStockQuantity(parseStockQuantityText(d.quantity)) ||
      draftSizeTexts(d).some(([, text]) => isInvalidSizeText(text))
    );
  });
}

/**
 * A linha da receita salva, aberta no editor. `toText` escreve o número como
 * o campo espera: o `<input type="number">` do painel quer ponto, o teclado do
 * app, vírgula.
 */
export function draftFromConsumable(
  c: PackageConsumable,
  toText: (n: number) => string = String,
): ConsumableDraft {
  const sizes = Object.fromEntries(
    VEHICLE_SIZES.flatMap((size) => {
      const q = c.sizeQuantities?.[size];
      return typeof q === 'number' ? [[size, toText(q)]] : [];
    }),
  ) as Partial<Record<VehicleSize, string>>;
  return {
    inventoryItemId: c.inventoryItemId,
    quantity: toText(c.quantity),
    sizes,
    showSizes: Object.keys(sizes).length > 0,
  };
}
